import React, { Fragment, useState, useEffect } from "react";
import { Badge, Button, Card, Col, Form, Row, Table, Pagination, Alert, Modal, InputGroup } from "react-bootstrap";

const StatusCardsManager = () => {
  const [selectedStatus, setSelectedStatus] = useState('delivered');
  const [loading, setLoading] = useState(false);
  const [cards, setCards] = useState([]);
  const [statusCounts, setStatusCounts] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [showUnblockModal, setShowUnblockModal] = useState(false);
  const pageSize = 20;

  // Configuration des statuts avec leurs propriétés visuelles
  const statusConfig = {
    'dataValidation': {
      label: 'Validation',
      icon: 'la-check-double',
      color: 'info',
      bgColor: 'rgba(23, 162, 184, 0.1)',
      borderColor: '#17a2b8',
      textColor: '#17a2b8'
    },
    'photoProcessing': {
      label: 'Traitement Photo',
      icon: 'la-image',
      color: 'primary',
      bgColor: 'rgba(0, 123, 255, 0.1)',
      borderColor: '#007bff',
      textColor: '#007bff'
    },
    'qualityControl': {
      label: 'Contrôle Qualité',
      icon: 'la-shield-alt',
      color: 'warning',
      bgColor: 'rgba(255, 193, 7, 0.1)',
      borderColor: '#ffc107',
      textColor: '#856404'
    },
    'printing': {
      label: 'Impression',
      icon: 'la-print',
      color: 'secondary',
      bgColor: 'rgba(108, 117, 125, 0.1)',
      borderColor: '#6c757d',
      textColor: '#6c757d'
    },
    'shipped': {
      label: 'Expédiées',
      icon: 'la-shipping-fast',
      color: 'warning',
      bgColor: 'rgba(255, 152, 0, 0.1)',
      borderColor: '#ff9800',
      textColor: '#e65100'
    },
    'delivered': {
      label: 'Livrées',
      icon: 'la-check-circle',
      color: 'success',
      bgColor: 'rgba(40, 167, 69, 0.1)',
      borderColor: '#28a745',
      textColor: '#28a745'
    },
    'blocked': {
      label: 'Bloquées',
      icon: 'la-ban',
      color: 'danger',
      bgColor: 'rgba(220, 53, 69, 0.1)',
      borderColor: '#dc3545',
      textColor: '#dc3545'
    },
    'cancelled': {
      label: 'Annulées',
      icon: 'la-times-circle',
      color: 'dark',
      bgColor: 'rgba(52, 58, 64, 0.1)',
      borderColor: '#343a40',
      textColor: '#343a40'
    }
  };

  useEffect(() => {
    loadStatusCounts();
  }, []);

  useEffect(() => {
    loadCardsByStatus();
  }, [selectedStatus, currentPage, searchTerm]);

  const loadStatusCounts = async () => {
    try {
      const baseUrl = process.env.REACT_APP_BASEURL_CLIENT_BACKEND;
      const response = await fetch(`${baseUrl}/Card/statistics/production`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (response.ok) {
        const stats = await response.json();
        // Utiliser les données réelles de l'API selon la réponse fournie
        const stepStats = stats.stepStatistics || {};
        setStatusCounts({
          dataValidation: stepStats.DataValidation || 0,
          photoProcessing: stepStats.PhotoProcessing || 0,
          qualityControl: stepStats.QualityControl || 0,
          printing: stepStats.Printing || 0,
          shipped: stepStats.Shipping || 0, // Utiliser "Shipping" de l'API
          delivered: stats.completedCards || 0,
          blocked: stats.failedCards || 0,
          cancelled: 0 // Pas dans l'API, garder à 0 ou calculer différemment
        });
      } else {
        throw new Error(`Erreur API: ${response.status}`);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des compteurs:', error);
      // Données de fallback en cas d'erreur
      setStatusCounts({
        dataValidation: 8, photoProcessing: 8, qualityControl: 0,
        printing: 1, shipped: 1, delivered: 0, blocked: 0, cancelled: 0
      });
    }
  };

  const loadCardsByStatus = async () => {
    setLoading(true);
    try {
      const baseUrl = process.env.REACT_APP_BASEURL_CLIENT_BACKEND;
      const params = new URLSearchParams({
        page: currentPage,
        pageSize: pageSize,
        status: getApiStatusName(selectedStatus),
        sortBy: 'ProductionDate',
        sortDirection: 'desc'
      });

      if (searchTerm) {
        params.append('searchTerm', searchTerm);
      }

      const response = await fetch(`${baseUrl}/Card/list?${params}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });

      if (response.ok) {
        const result = await response.json();
        setCards(result.data || []);
        setTotalPages(result.totalPages || 0);
      } else {
        const statusResponse = await fetch(`${baseUrl}/Card/status/${getApiStatusName(selectedStatus)}?pageNumber=${currentPage}&pageSize=${pageSize}`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        });
        
        if (statusResponse.ok) {
          const statusCards = await statusResponse.json();
          const filteredCards = searchTerm 
            ? statusCards.filter(card => 
                card.cardNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                card.beneficiaryName?.toLowerCase().includes(searchTerm.toLowerCase())
              )
            : statusCards;
          
          setCards(filteredCards);
          setTotalPages(Math.ceil(filteredCards.length / pageSize));
        }
      }
    } catch (error) {
      console.error('Erreur lors du chargement des cartes:', error);
      setCards([]);
    } finally {
      setLoading(false);
    }
  };

  const getApiStatusName = (status) => {
    const mapping = {
      'dataValidation': 'DataValidation',
      'photoProcessing': 'PhotoProcessing',
      'qualityControl': 'QualityControl',
      'printing': 'Printing',
      'shipped': 'Shipped',
      'delivered': 'Delivered',
      'blocked': 'Blocked',
      'cancelled': 'Cancelled'
    };
    return mapping[status] || 'Delivered';
  };

  const handleCardAction = (card, action) => {
    setSelectedCard(card);
    if (action === 'details') {
      setShowDetailsModal(true);
    } else if (action === 'block') {
      setShowBlockModal(true);
    } else if (action === 'unblock') {
      setShowUnblockModal(true);
    }
  };

  const renderStatusCards = () => {
    return (
      <Row className="g-3 mb-4">
        {Object.entries(statusConfig).map(([statusKey, config]) => (
          <Col xl={3} lg={4} md={6} sm={12} key={statusKey}>
            <div 
              className={`status-card h-100 ${selectedStatus === statusKey ? 'active' : ''}`}
              onClick={() => {
                setSelectedStatus(statusKey);
                setCurrentPage(1);
                setSearchTerm('');
              }}
              style={{
                background: selectedStatus === statusKey ? config.bgColor : '#ffffff',
                border: `2px solid ${selectedStatus === statusKey ? config.borderColor : '#e9ecef'}`,
                borderRadius: '12px',
                padding: '1.5rem',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: selectedStatus === statusKey ? `0 4px 15px ${config.borderColor}40` : '0 2px 8px rgba(0,0,0,0.08)',
                transform: selectedStatus === statusKey ? 'translateY(-2px)' : 'none'
              }}
              onMouseEnter={(e) => {
                if (selectedStatus !== statusKey) {
                  e.target.style.transform = 'translateY(-1px)';
                  e.target.style.boxShadow = '0 4px 12px rgba(0,0,0,0.12)';
                }
              }}
              onMouseLeave={(e) => {
                if (selectedStatus !== statusKey) {
                  e.target.style.transform = 'none';
                  e.target.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
                }
              }}
            >
              <div className="d-flex align-items-center mb-3">
                <div 
                  className="status-icon rounded-circle d-flex align-items-center justify-content-center text-white"
                  style={{
                    width: '50px',
                    height: '50px',
                    background: `linear-gradient(135deg, ${config.borderColor}ee, ${config.borderColor})`
                  }}
                >
                  <i className={`la ${config.icon} fs-4`}></i>
                </div>
                <div className="flex-grow-1 text-end">
                  {selectedStatus === statusKey && (
                    <div className="pulse-dot" style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      background: config.borderColor,
                      animation: 'pulse 2s infinite'
                    }}></div>
                  )}
                </div>
              </div>
              
              <div className="mb-2">
                <h3 className="fw-bold mb-1" style={{ color: config.textColor, fontSize: '2rem' }}>
                  {(statusCounts[statusKey] || 0).toLocaleString()}
                </h3>
                <h6 className="mb-0 fw-medium" style={{ color: config.textColor }}>
                  {config.label}
                </h6>
              </div>
              
              <div className="status-progress" style={{
                height: '4px',
                background: '#e9ecef',
                borderRadius: '2px',
                overflow: 'hidden'
              }}>
                <div style={{
                  width: selectedStatus === statusKey ? '100%' : '0%',
                  height: '100%',
                  background: `linear-gradient(90deg, ${config.borderColor}, ${config.borderColor}aa)`,
                  transition: 'width 0.5s ease'
                }}></div>
              </div>
            </div>
          </Col>
        ))}
      </Row>
    );
  };

  const renderCardsTable = () => {
    const config = statusConfig[selectedStatus];
    
    return (
      <Card className="border-0 shadow-sm">
        <Card.Header 
          className="border-0 py-4"
          style={{ background: `linear-gradient(135deg, ${config.borderColor}15, ${config.bgColor})` }}
        >
          <Row className="align-items-center">
            <Col lg={6}>
              <div className="d-flex align-items-center">
                <div 
                  className="rounded-circle d-flex align-items-center justify-content-center text-white me-3"
                  style={{
                    width: '50px',
                    height: '50px',
                    background: `linear-gradient(135deg, ${config.borderColor}, ${config.borderColor}dd)`
                  }}
                >
                  <i className={`la ${config.icon} fs-4`}></i>
                </div>
                <div>
                  <h5 className="mb-1 fw-bold" style={{ color: config.textColor }}>
                    Cartes {config.label}
                  </h5>
                  <div className="d-flex align-items-center">
                    <Badge bg={config.color} className="me-2">
                      {(statusCounts[selectedStatus] || 0).toLocaleString()} cartes
                    </Badge>
                    <small className="text-muted">
                      Page {currentPage} sur {totalPages}
                    </small>
                  </div>
                </div>
              </div>
            </Col>
            <Col lg={6}>
              <div className="search-container">
                <InputGroup>
                  <InputGroup.Text className="bg-white border-end-0">
                    <i className="la la-search text-muted"></i>
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Rechercher par numéro de carte ou bénéficiaire..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="border-start-0"
                    style={{ borderColor: '#dee2e6' }}
                  />
                  {searchTerm && (
                    <Button 
                      variant="outline-secondary" 
                      onClick={() => {
                        setSearchTerm('');
                        setCurrentPage(1);
                      }}
                    >
                      <i className="la la-times"></i>
                    </Button>
                  )}
                </InputGroup>
              </div>
            </Col>
          </Row>
        </Card.Header>
        
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border" style={{ color: config.borderColor }} role="status">
              </div>
              <div className="mt-3 text-muted">Chargement des cartes {config.label.toLowerCase()}...</div>
            </div>
          ) : cards.length === 0 ? (
            <div className="text-center py-5">
              <div 
                className="empty-state-icon mb-3"
                style={{ color: config.borderColor, opacity: '0.3' }}
              >
                <i className={`la ${config.icon} la-5x`}></i>
              </div>
              <h6 className="text-muted">Aucune carte {config.label.toLowerCase()}</h6>
              <p className="text-muted mb-0">
                {searchTerm 
                  ? `Aucune carte ne correspond à votre recherche "${searchTerm}"`
                  : `Aucune carte avec le statut "${config.label}" trouvée`
                }
              </p>
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <Table hover className="mb-0 align-middle">
                  <thead style={{ background: config.bgColor }}>
                    <tr>
                      <th className="border-0 fw-bold py-3" style={{ color: config.textColor }}>
                        <i className="la la-id-card me-2"></i>
                        Numéro de Carte
                      </th>
                      <th className="border-0 fw-bold py-3" style={{ color: config.textColor }}>
                        <i className="la la-user me-2"></i>
                        Bénéficiaire
                      </th>
                      <th className="border-0 fw-bold py-3" style={{ color: config.textColor }}>
                        <i className="la la-calendar me-2"></i>
                        Date Production
                      </th>
                      <th className="border-0 fw-bold py-3" style={{ color: config.textColor }}>
                        <i className="la la-flag me-2"></i>
                        Statut
                      </th>
                      <th className="border-0 fw-bold py-3" style={{ color: config.textColor }}>
                        <i className="la la-info-circle me-2"></i>
                        Informations
                      </th>
                      <th className="border-0 fw-bold py-3 text-center" style={{ color: config.textColor }}>
                        <i className="la la-cogs me-2"></i>
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {cards.map((card, index) => (
                      <tr key={card.id || index} className="border-bottom">
                        <td className="py-3">
                          <div className="d-flex align-items-center">
                            <div 
                              className="status-indicator me-3"
                              style={{
                                width: '4px',
                                height: '45px',
                                background: config.borderColor,
                                borderRadius: '2px'
                              }}
                            ></div>
                            <div>
                              <div className="fw-bold font-monospace fs-6" style={{ color: '#2c3e50' }}>
                                {card.cardNumber}
                              </div>
                              <small className="text-muted">ID: {card.id}</small>
                            </div>
                          </div>
                        </td>
                        <td className="py-3">
                          <div className="fw-medium text-dark">
                            {card.beneficiaryName || `Bénéficiaire ${card.beneficiaryId}`}
                          </div>
                          <small className="text-muted">ID: {card.beneficiaryId}</small>
                        </td>
                        <td className="py-3">
                          <div className="text-dark">
                            {card.productionDate ? new Date(card.productionDate).toLocaleDateString('fr-FR') : 'N/A'}
                          </div>
                          {card.productionDate && (
                            <small className="text-muted">
                              {new Date(card.productionDate).toLocaleTimeString('fr-FR', {hour: '2-digit', minute: '2-digit'})}
                            </small>
                          )}
                        </td>
                        <td className="py-3">
                          <Badge bg={config.color} className="px-3 py-2">
                            <i className={`la ${config.icon} me-1`}></i>
                            {config.label}
                          </Badge>
                        </td>
                        <td className="py-3">
                          <div className="info-container">
                            {card.trackingNumber && (
                              <div className="text-info mb-1">
                                <i className="la la-shipping-fast me-1"></i>
                                <small>{card.trackingNumber}</small>
                              </div>
                            )}
                            {card.deliveryDate && (
                              <div className="text-success mb-1">
                                <i className="la la-check-circle me-1"></i>
                                <small>{new Date(card.deliveryDate).toLocaleDateString('fr-FR')}</small>
                              </div>
                            )}
                            {card.blockReason && (
                              <div className="text-danger mb-1">
                                <i className="la la-exclamation-triangle me-1"></i>
                                <small>{card.blockReason}</small>
                              </div>
                            )}
                            {!card.trackingNumber && !card.deliveryDate && !card.blockReason && (
                              <small className="text-muted">
                                <i className="la la-clock me-1"></i>
                                En cours de traitement
                              </small>
                            )}
                          </div>
                        </td>
                        <td className="py-3 text-center">
                          <div className="btn-group btn-group-sm" role="group">
                            <Button 
                              variant="outline-primary" 
                              size="sm" 
                              title="Voir détails"
                              onClick={() => handleCardAction(card, 'details')}
                            >
                              <i className="la la-eye"></i>
                            </Button>
                            {selectedStatus === 'blocked' ? (
                              <Button 
                                variant="outline-success" 
                                size="sm" 
                                title="Débloquer"
                                onClick={() => handleCardAction(card, 'unblock')}
                              >
                                <i className="la la-unlock"></i>
                              </Button>
                            ) : selectedStatus !== 'delivered' && selectedStatus !== 'cancelled' && (
                              <Button 
                                variant="outline-danger" 
                                size="sm" 
                                title="Bloquer"
                                onClick={() => handleCardAction(card, 'block')}
                              >
                                <i className="la la-ban"></i>
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
              
              {totalPages > 1 && (
                <div className="p-4 border-top bg-light">
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="text-muted">
                      <small>
                        Affichage {((currentPage - 1) * pageSize) + 1} à {Math.min(currentPage * pageSize, cards.length * totalPages)} 
                        sur {(statusCounts[selectedStatus] || 0).toLocaleString()} cartes
                      </small>
                    </div>
                    <Pagination className="mb-0">
                      <Pagination.First 
                        onClick={() => setCurrentPage(1)} 
                        disabled={currentPage === 1} 
                      />
                      <Pagination.Prev 
                        onClick={() => setCurrentPage(currentPage - 1)} 
                        disabled={currentPage === 1} 
                      />
                      
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        const startPage = Math.max(1, currentPage - 2);
                        const page = startPage + i;
                        if (page <= totalPages) {
                          return (
                            <Pagination.Item
                              key={page}
                              active={page === currentPage}
                              onClick={() => setCurrentPage(page)}
                            >
                              {page}
                            </Pagination.Item>
                          );
                        }
                        return null;
                      })}
                      
                      <Pagination.Next 
                        onClick={() => setCurrentPage(currentPage + 1)} 
                        disabled={currentPage === totalPages} 
                      />
                      <Pagination.Last 
                        onClick={() => setCurrentPage(totalPages)} 
                        disabled={currentPage === totalPages} 
                      />
                    </Pagination>
                  </div>
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>
    );
  };

  return (
    <Fragment>
      <style>
        {`
          @keyframes pulse {
            0% { opacity: 1; }
            50% { opacity: 0.5; }
            100% { opacity: 1; }
          }
          
          .status-card {
            position: relative;
            overflow: hidden;
          }
          
          .status-card::before {
            content: '';
            position: absolute;
            top: 0;
            left: -100%;
            width: 100%;
            height: 2px;
            background: linear-gradient(90deg, transparent, rgba(255,255,255,0.8), transparent);
            transition: left 0.5s;
          }
          
          .status-card:hover::before {
            left: 100%;
          }
          
          .table tbody tr {
            transition: all 0.2s ease;
          }
          
          .table tbody tr:hover {
            background-color: rgba(0,0,0,0.02);
            transform: translateX(2px);
          }
          
          .btn-group .btn {
            transition: all 0.2s ease;
          }
          
          .btn-group .btn:hover {
            transform: translateY(-1px);
            box-shadow: 0 4px 8px rgba(0,0,0,0.15);
          }
          
          .search-container .form-control:focus {
            box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.15);
            border-color: #86b7fe;
          }
        `}
      </style>
      
      {renderStatusCards()}
      {renderCardsTable()}

      {/* Modal de détails */}
      <Modal show={showDetailsModal} onHide={() => setShowDetailsModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="la la-id-card me-2"></i>
            Détails de la carte
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedCard && (
            <Row>
              <Col md={6}>
                <h6 className="text-muted mb-3">Informations générales</h6>
                <div className="mb-2">
                  <strong>Numéro de carte:</strong> <span className="font-monospace">{selectedCard.cardNumber}</span>
                </div>
                <div className="mb-2">
                  <strong>Bénéficiaire:</strong> {selectedCard.beneficiaryName || `ID: ${selectedCard.beneficiaryId}`}
                </div>
                <div className="mb-2">
                  <strong>Statut:</strong> <Badge bg={statusConfig[selectedStatus].color}>{statusConfig[selectedStatus].label}</Badge>
                </div>
              </Col>
              <Col md={6}>
                <h6 className="text-muted mb-3">Dates importantes</h6>
                <div className="mb-2">
                  <strong>Production:</strong> {selectedCard.productionDate ? new Date(selectedCard.productionDate).toLocaleString('fr-FR') : 'N/A'}
                </div>
                {selectedCard.deliveryDate && (
                  <div className="mb-2">
                    <strong>Livraison:</strong> {new Date(selectedCard.deliveryDate).toLocaleString('fr-FR')}
                  </div>
                )}
                {selectedCard.trackingNumber && (
                  <div className="mb-2">
                    <strong>Suivi:</strong> <span className="font-monospace">{selectedCard.trackingNumber}</span>
                  </div>
                )}
              </Col>
            </Row>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetailsModal(false)}>
            Fermer
          </Button>
        </Modal.Footer>
      </Modal>
    </Fragment>
  );
};

export default StatusCardsManager;